import { QueryKey, useMutation } from '@tanstack/react-query';
import { ApiError } from '../@types';
import { useToast } from '../contexts/ToastContext';
import { ApiService, deleteData, MethodsHeaders, patchData, postData } from '../utils/api';
import { useInvalidateQuery } from './useInvalidateQuery';
import { useToastErrorMessage } from './useToastErrorMessage';
import { RawAxiosRequestHeaders, AxiosHeaders } from 'axios';

const useMutateData = (
  path: string,
  queryKey: QueryKey,
  apiService?: ApiService,
  disableSnackbar = false,
  headers?: (RawAxiosRequestHeaders & MethodsHeaders) | AxiosHeaders
) => {
  const { handleErrorMessage } = useToastErrorMessage();
  const { enqueueSnackbar } = useToast();
  const invalidateData = useInvalidateQuery();

  const handleSnackbar = (msg: string) => {
    if (disableSnackbar) {
      return;
    } else {
      enqueueSnackbar({ message: msg || 'Item deleted successfully.', variant: 'success' });
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: unknown) => {
      await postData(path, data, apiService, headers);
    },

    onSuccess: async () => {
      await invalidateData(queryKey);
    },

    onError: (error: ApiError) => apiService !== 'kc' && handleErrorMessage(error),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteData(`${path}/${id}`, apiService);
    },

    onSuccess: async () => {
      await invalidateData(queryKey);
    },

    onError: (error: ApiError) => handleErrorMessage(error),
  });

  const editMutation = useMutation({
    mutationFn: async (params: { id: string; data: unknown }) => {
      const { id, data } = params;
      await patchData(`${path}/${id}`, data, apiService);
    },

    onSuccess: async () => {
      await invalidateData(queryKey);
    },

    onError: (error: ApiError) => {
      return handleErrorMessage(error);
    },
  });

  const createWithPathMutation = useMutation({
    mutationFn: async (params: { customPath: string; data: unknown }) => {
      await postData(params.customPath, params.data, apiService, headers);
    },

    onSuccess: async () => {
      await invalidateData(queryKey);
    },

    onError: (error: ApiError) => apiService !== 'kc' && handleErrorMessage(error),
  });

  const isLoading =
    createMutation.isPending || deleteMutation.isPending || editMutation.isPending || createWithPathMutation.isPending;

  return {
    createData: (data: unknown, onSuccess?: () => void, successMessage?: string, onSettled?: () => void) =>
      createMutation.mutate(data, {
        onSuccess: async () => {
          handleSnackbar(successMessage || 'Created successfully.');
          onSuccess?.();
        },
        onSettled: () => {
          onSettled?.();
        },
      }),
    createDataAsync: async (data: unknown, onSuccess?: () => void, successMessage?: string) => {
      await createMutation.mutateAsync(data);
      handleSnackbar(successMessage || 'Created successfully.');

      onSuccess?.();
    },
    deleteData: (id: string, onSuccess?: () => void, successMessage?: string) =>
      deleteMutation.mutate(id, {
        onSuccess: async () => {
          handleSnackbar(successMessage || 'Deleted successfully.');

          onSuccess?.();
        },
      }),
    updateData: (id: string, data: unknown, onSuccess?: () => void, successMessage?: string) =>
      editMutation.mutate(
        { id, data },
        {
          onSuccess: async () => {
            handleSnackbar(successMessage || 'Updated successfully.');
            onSuccess?.();
          },
        }
      ),
    updateDataAsync: async (id: string, data: unknown, onSuccess?: () => void, successMessage?: string) => {
      await editMutation.mutateAsync({ id, data });
      handleSnackbar(successMessage || 'Updated successfully.');
      onSuccess?.();
    },
    createDataWithPath: (
      customPath: string,
      data: unknown,
      onSuccess?: () => void,
      successMessage?: string,
      onSettled?: () => void
    ) =>
      createWithPathMutation.mutate(
        { customPath, data },
        {
          onSuccess: async () => {
            handleSnackbar(successMessage || 'Created successfully.');
            onSuccess?.();
          },
          onSettled: () => {
            onSettled?.();
          },
        }
      ),
    isLoading,
  };
};

export default useMutateData;
